import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DELETE_REASON_CHOICES } from "@/components/DeleteRecordButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader, Disclaimer } from "@/components/Common";
import { SearchableSelect } from "@/components/Controlled";
import { Save, Trash2, FilePlus2, MessageSquare, NotebookText, Send } from "lucide-react";
import { toast } from "sonner";

// Correction 5 + 6 — Beta Operations admin view.
// Lightweight read/review of two NEW collections:
//   - feedback_submissions  (from end users via the floating widget)
//   - internal_notes        (admin-only observation log; planning artefact)
// No new workflow, no new roles. Super Admin only.

export default function BetaOperations() {
  return (
    <div data-testid="beta-operations-page">
      <PageHeader
        title="Beta Operations"
        subtitle="Live-beta feedback review and internal observation log. Documentation tools only — no workflow changes."
      />
      <Tabs defaultValue="feedback">
        <TabsList className="mb-3">
          <TabsTrigger value="feedback" data-testid="beta-tab-feedback">
            <MessageSquare className="w-4 h-4 mr-2" /> Client / User Feedback
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="beta-tab-notes">
            <NotebookText className="w-4 h-4 mr-2" /> Internal Notes
          </TabsTrigger>
        </TabsList>
        <TabsContent value="feedback"><FeedbackTab /></TabsContent>
        <TabsContent value="notes"><InternalNotesTab /></TabsContent>
      </Tabs>
      <Disclaimer>
        Information gathering only. Do not treat feedback or internal notes as workflow instructions.
        Future planning items will be reviewed for Kandake 1.5 / 2.0 / Makeda releases.
      </Disclaimer>
    </div>
  );
}

function FeedbackTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editingStatus, setEditingStatus] = useState({}); // feedback_id -> { status, admin_notes }

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/feedback", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  async function saveStatus(rec) {
    const patch = editingStatus[rec.feedback_id] || {};
    const next = {
      status: patch.status || rec.status || "New",
      admin_notes: patch.admin_notes ?? rec.admin_notes ?? "",
    };
    try {
      await api.put(`/feedback/${rec.feedback_id}/status`, next);
      toast.success("Updated");
      setEditingStatus((prev) => {
        const cp = { ...prev };
        delete cp[rec.feedback_id];
        return cp;
      });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-5 py-4 border-b flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <Label className="text-xs text-muted-foreground">Filter by status</Label>
            <div className="mt-1">
              <SearchableSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: "", label: "All statuses" },
                  { value: "New", label: "New" },
                  { value: "Acknowledged", label: "Acknowledged" },
                  { value: "Archived", label: "Archived" },
                ]}
                placeholder="All statuses"
                allowClear={false}
                testid="feedback-status-filter"
              />
            </div>
          </div>
          <span className="text-xs text-muted-foreground self-end pb-2 ml-auto" data-testid="feedback-count">
            {items.length} submission{items.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="overflow-auto">
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Submitted</TableHead>
                <TableHead className="w-[180px]">Submitted by</TableHead>
                <TableHead className="w-[150px]">Category</TableHead>
                <TableHead>Message</TableHead>
                <TableHead className="w-[180px]">Page</TableHead>
                <TableHead className="w-[150px]">Status</TableHead>
                <TableHead className="w-[110px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No feedback submissions yet.</TableCell></TableRow>
              )}
              {items.map((rec) => {
                const patch = editingStatus[rec.feedback_id] || {};
                const status = patch.status ?? rec.status ?? "New";
                const notes = patch.admin_notes ?? rec.admin_notes ?? "";
                const dirty = patch.status !== undefined || patch.admin_notes !== undefined;
                return (
                  <TableRow key={rec.feedback_id} data-testid={`feedback-row-${rec.feedback_id}`} className="align-top">
                    <TableCell className="font-mono text-[11px] text-muted-foreground">{(rec.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="text-xs break-all">
                      {rec.submitted_by_email}
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{rec.submitted_by_role}</div>
                    </TableCell>
                    <TableCell className="text-xs">{rec.category}</TableCell>
                    <TableCell className="text-sm break-words whitespace-pre-wrap">{rec.message}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground break-all">{rec.page_context || "—"}</TableCell>
                    <TableCell>
                      <SearchableSelect
                        value={status}
                        onChange={(v) => setEditingStatus((prev) => ({ ...prev, [rec.feedback_id]: { ...(prev[rec.feedback_id] || {}), status: v } }))}
                        options={["New", "Acknowledged", "Archived"]}
                        placeholder="Status"
                        allowClear={false}
                        testid={`feedback-status-${rec.feedback_id}`}
                      />
                      <Textarea
                        value={notes}
                        onChange={(e) => setEditingStatus((prev) => ({ ...prev, [rec.feedback_id]: { ...(prev[rec.feedback_id] || {}), admin_notes: e.target.value } }))}
                        rows={2}
                        placeholder="Admin note (optional)"
                        className="mt-2 text-xs"
                        data-testid={`feedback-admin-notes-${rec.feedback_id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" disabled={!dirty} onClick={() => saveStatus(rec)} data-testid={`feedback-save-${rec.feedback_id}`}>
                        <Save className="w-3.5 h-3.5 mr-1.5" /> Save
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function InternalNotesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagFilter, setTagFilter] = useState("");
  const [tagOptions, setTagOptions] = useState([
    "Observation",
    "Workflow Friction",
    "Client Comment (Phone)",
    "Feature Idea — 1.5",
    "Feature Idea — 2.0",
    "Feature Idea — Makeda",
    "Beta Test Note",
    "General",
  ]);
  const [newBody, setNewBody] = useState("");
  const [newTag, setNewTag] = useState("Observation");
  const [posting, setPosting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingDeleteReason, setPendingDeleteReason] = useState("");
  const [pendingDeleteOther, setPendingDeleteOther] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/internal-notes", { params: tagFilter ? { tag: tagFilter } : {} });
      setItems(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    api.get("/internal-notes/options")
      .then((r) => { if (Array.isArray(r.data?.tags)) setTagOptions(r.data.tags); })
      .catch(() => { /* keep fallback */ });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tagFilter]);

  async function addNote() {
    const body = (newBody || "").trim();
    if (!body) { toast.error("Note body cannot be empty."); return; }
    setPosting(true);
    try {
      await api.post("/internal-notes", { body, tag: newTag || "General" });
      toast.success("Note recorded");
      setNewBody("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPosting(false); }
  }

  async function removeNote(rec, reason) {
    try {
      const qs = new URLSearchParams();
      qs.set("reason", reason || "No reason recorded");
      qs.set("force", "true");
      await api.delete(`/internal-notes/${rec.note_id}?${qs.toString()}`);
      toast.success("Note deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <div className="space-y-4">
      {/* Create new */}
      <Card>
        <CardContent className="p-5">
          <div className="font-display text-base mb-3">Record an observation</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <Label className="text-xs">Tag</Label>
              <div className="mt-1">
                <SearchableSelect
                  value={newTag}
                  onChange={(v) => setNewTag(v || "General")}
                  options={tagOptions}
                  placeholder="Tag"
                  allowClear={false}
                  testid="internal-note-tag-select"
                />
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Feature ideas tagged for 1.5 / 2.0 / Makeda are captured for future planning only.
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Note body</Label>
              <Textarea
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                rows={4}
                placeholder="Observation, friction, client phone comment, or future feature idea…"
                className="mt-1"
                data-testid="internal-note-body-input"
              />
              <div className="mt-3 flex justify-end">
                <Button onClick={addNote} disabled={posting || !newBody.trim()} data-testid="internal-note-submit" style={{ background: "hsl(347 72% 22%)", color: "white" }}>
                  <FilePlus2 className="w-4 h-4 mr-2" />{posting ? "Recording…" : "Record note"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex flex-wrap items-end gap-3">
            <div className="min-w-[240px]">
              <Label className="text-xs text-muted-foreground">Filter by tag</Label>
              <div className="mt-1">
                <SearchableSelect
                  value={tagFilter}
                  onChange={setTagFilter}
                  options={[{ value: "", label: "All tags" }, ...tagOptions.map((t) => ({ value: t, label: t }))]}
                  placeholder="All tags"
                  allowClear={false}
                  testid="internal-note-tag-filter"
                />
              </div>
            </div>
            <span className="text-xs text-muted-foreground self-end pb-2 ml-auto" data-testid="internal-note-count">
              {items.length} note{items.length === 1 ? "" : "s"}
            </span>
          </div>
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Recorded</TableHead>
                <TableHead className="w-[180px]">Author</TableHead>
                <TableHead className="w-[180px]">Tag</TableHead>
                <TableHead>Body</TableHead>
                <TableHead className="w-[80px] text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No internal notes recorded yet.</TableCell></TableRow>
              )}
              {items.map((rec) => (
                <TableRow key={rec.note_id} data-testid={`internal-note-row-${rec.note_id}`} className="align-top">
                  <TableCell className="font-mono text-[11px] text-muted-foreground">{(rec.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell className="text-xs break-all">
                    {rec.author_name || rec.author_email}
                    <div className="font-mono text-[10px] text-muted-foreground">{rec.author_email}</div>
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-block px-2 py-0.5 rounded text-[11px]"
                      style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}
                    >
                      {rec.tag || "General"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm break-words whitespace-pre-wrap">{rec.body}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setPendingDelete(rec)}
                      data-testid={`internal-note-delete-${rec.note_id}`}
                      aria-label="Delete note"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) { setPendingDelete(null); setPendingDeleteReason(""); setPendingDeleteOther(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this internal note?</AlertDialogTitle>
            <AlertDialogDescription>
              The note will be permanently removed from the observation log. A deletion is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason (required · captured in audit)
              </Label>
              <Select value={pendingDeleteReason} onValueChange={setPendingDeleteReason}>
                <SelectTrigger className="mt-1 text-[12.5px]" data-testid="internal-note-delete-reason-trigger">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_REASON_CHOICES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pendingDeleteReason === "Other" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Explanation (required when reason is Other)
                </Label>
                <Textarea
                  value={pendingDeleteOther}
                  onChange={(e) => setPendingDeleteOther(e.target.value)}
                  rows={2}
                  className="mt-1 text-[12.5px]"
                  data-testid="internal-note-delete-reason-other"
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="internal-note-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingDelete || !pendingDeleteReason) return;
                if (pendingDeleteReason === "Other" && (pendingDeleteOther || "").trim().length < 3) return;
                const finalReason = pendingDeleteReason === "Other" ? `Other: ${pendingDeleteOther.trim()}` : pendingDeleteReason;
                removeNote(pendingDelete, finalReason);
                setPendingDelete(null);
                setPendingDeleteReason(""); setPendingDeleteOther("");
              }}
              disabled={!pendingDeleteReason || (pendingDeleteReason === "Other" && (pendingDeleteOther || "").trim().length < 3)}
              data-testid="internal-note-delete-confirm"
              style={{ background: "hsl(0 70% 42%)", color: "white" }}
            >
              Delete note
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
